import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

const BankingPosition = React.lazy(() => import("./BankingPosition"));
const BankingAccountPage = React.lazy(() => import("./BankingAccountPage"));
const BankingTransactionPage = React.lazy(() => import("./BankingTransactionPage"));
const BankingNotifications = React.lazy(() => import("./BankingNotifications"));
const BankingActivity = React.lazy(() => import("./BankingActivity"));
const BankingActivityEvent = React.lazy(() => import("./BankingActivityEvent"));
const BankingSettings = React.lazy(() => import("./BankingSettings"));
const BankingMonth = React.lazy(() => import("./BankingMonth"));
const BankingWealth = React.lazy(() => import("./BankingWealth"));

const bankingModuleConfig: ModuleConfig = {
  id: "banking",
  name: "Banking",
  description: "Live bank balances, transactions, alerts and digests from your connected accounts",
  version: "0.3.0",
  enabled: true,
  category: "business",
  order: 7,
  icon: "🏦",
  // eslint-disable-next-line no-restricted-syntax -- module tile accent; matches the other moduleConfigs, which all carry a literal here
  color: "#0D9488",

  routes: [
    // Data-table rule 7: list pages with a data table take the full page width so
    // the table flexes into it (see transcriptions/moduleConfig.ts).
    { path: "/banking", component: BankingPosition, permissionLevel: "view", useLayout: true, layoutOptions: { maxW: "100%" } },
    { path: "/banking/accounts/:id", component: BankingAccountPage, permissionLevel: "view", useLayout: true, layoutOptions: { maxW: "100%", py: 4 } },
    { path: "/banking/transactions/:id", component: BankingTransactionPage, permissionLevel: "view", useLayout: true, layoutOptions: { maxW: "container.lg", py: 4 } },
    { path: "/banking/notifications", component: BankingNotifications, permissionLevel: "view", useLayout: true, layoutOptions: { maxW: "container.lg", py: 6 } },
    // Admin-only. `permissionLevel: "admin"` resolves from the backend manifest's
    // `admin` key (permissions.ts) — the manifest MUST carry it or this resolves to [].
    // The literal `/banking/activity` precedes `:id` so the param cannot swallow it.
    { path: "/banking/activity", component: BankingActivity, permissionLevel: "admin", useLayout: true, layoutOptions: { maxW: "container.xl", py: 6 } },
    { path: "/banking/activity/:id", component: BankingActivityEvent, permissionLevel: "admin", useLayout: true, layoutOptions: { maxW: "container.lg", py: 4 } },
    { path: "/banking/settings", component: BankingSettings, permissionLevel: "admin", useLayout: true, layoutOptions: { maxW: "container.lg", py: 6 } },
    // The Month Game (0.2.0): buckets, trajectory and money moves — admin-only, `?month=YYYY-MM`.
    { path: "/banking/month", component: BankingMonth, permissionLevel: "admin", useLayout: true, layoutOptions: { maxW: "100%" } },
    // What I have (0.3.0): cash + gold + bitcoin − IOUs, in AUD / USD / gold oz — admin-only.
    { path: "/banking/wealth", component: BankingWealth, permissionLevel: "admin", useLayout: true, layoutOptions: { maxW: "100%" } },
  ],

  navigation: [
    { label: "Position", path: "/banking", icon: "🏦", permissionLevel: "view" },
    { label: "Month", path: "/banking/month", icon: "📅", permissionLevel: "admin" },
    { label: "What I have", path: "/banking/wealth", icon: "🪙", permissionLevel: "admin" },
    { label: "Notifications", path: "/banking/notifications", icon: "🔔", permissionLevel: "view" },
    { label: "Activity", path: "/banking/activity", icon: "📜", permissionLevel: "admin" },
    { label: "Settings", path: "/banking/settings", icon: "⚙️", permissionLevel: "admin" },
  ],

  // Resolved from the backend SSOT (src/modules/banking/permissions.ts) at runtime — ships EMPTY on purpose.
  // `admin` is a custom key the ModuleConfig type allows (`[key: string]: string[]`).
  permissions: { view: [], create: [], edit: [], delete: [], admin: [] },

  quickActions: [
    { label: "This month", path: "/banking/month", icon: "📅", description: "Safe to spend today, tax and save buckets, and the jobs that move the month" },
    { label: "What I have", path: "/banking/wealth", icon: "🪙", description: "Cash, gold and bitcoin less what you owe people — in AUD, USD and ounces of gold" },
    { label: "Connect Redbark", path: "/banking/settings", icon: "🔌", description: "Connect the bank feed and choose who sees which account" },
    { label: "My alerts", path: "/banking/notifications", icon: "🔔", description: "Alerts when money moves, and daily / weekly / monthly digests" },
  ],

  changelog: [
    {
      version: "0.3.0",
      releaseDate: "2026-09-23",
      title: "Close the month, gold vs bitcoin, what I have, forward or backward — plus rent that ticks itself off",
      features: [
        "✅ Close the month (from the 25th, or any past month): the money frozen, gold and bitcoin start → end in AUD, and Simon's tilt — 70% into whichever fell more, never 100/0. Re-closing keeps your decision",
        "✅ Record what you did with the surplus (gold in grams, bitcoin in sats) — it becomes your holdings. Nothing is ever bought here",
        "✅ What I have: cash − what you owe people + gold + bitcoin, in AUD, US dollars and ounces of gold, with a purchase / sale log and hand-entered prices",
        "✅ Forward or backward: a month against the close before it, tile by tile, and a 12-month strip of arrows; an unclosed month says so, never a zero",
        "✅ Standing money moves: rent every Monday appears in each month by itself and is ticked off only when the matching bank row posts, linked to it",
        "✅ Foreign-currency accounts: Revolut's USD pocket is stored as US dollars (from its name), converted at the day's rate in the net position, and left out of the month's AUD sums",
        "✅ Gold, bitcoin and AUD/USD prices stored once a day by the bank feed",
      ],
      notes: "Gold has no free price history: a past day with no stored gold price needs one entered by hand (August 2026 was seeded from COMEX closes). Streaks, levels and nudges are the next release.",
    },
    {
      version: "0.2.0",
      releaseDate: "2026-09-21",
      title: "The Month Game — tax bucket, save level, safe to spend, and the jobs that move the month",
      features: [
        "✅ Month page (admin): safe to spend per day after tax and a save target, with the three buckets and a funded bar that reads the tax account's REAL balance",
        "✅ Income rules: say what each credit is (client receipt, wage, household, asset sold, internal move) — tax is set aside only from receipts with nothing withheld",
        "✅ Trajectory: actual income minus spend to today, where the month lands on the plan, and where it lands if the open jobs get done — assumptions printed under the chart",
        "✅ Money moves beside the line: today's one thing first, due day then A–Z until you arrange them, done is not landed until you say the money moved, roll open jobs to next month",
        "✅ Position page shows this month's safe-to-spend and tax bucket in one line",
        "✅ Check the bank now on the position page — a manual poll that reports what the server found",
        "✅ IOUs: mark a credit as borrowed from a person (they get a BB Token and a Redeem for AUD button), mark a debit as paying them back; the month and the position leave loans out of income, spend and the net",
      ],
      notes: "Month close with gold and bitcoin prices, holdings and month-vs-month compare are the next release (plan 2026-09-21).",
    },
    {
      version: "0.1.0",
      releaseDate: "2026-09-11",
      title: "Initial release — a live position from Redbark, shared account by account",
      features: [
        "✅ Connect Redbark with a write-only key; verify the plan and scopes before anything is stored",
        "✅ Accounts discovered automatically; enable, nickname, mark liabilities and choose what counts toward the position",
        "✅ Explicit per-account grants — a person sees only the accounts shared with them, and is told when one is",
        "✅ Webhook feed with a 15-minute poll as the safety net, and a backfill that never fires alerts",
        "✅ Position page: net position with one honest 'as of', per-account cards with freshness, period snapshot and recent transactions",
        "✅ Account page: period snapshot, a flexing transactions table with filters, and CSV export",
        "✅ Transaction page: every field in Brisbane time, similar transactions, comments with receipt drops",
        "✅ Alerts when money moves (direction, amount band, keyword) by email or SMS, with a 'send me a sample'",
        "✅ Daily / weekly / monthly digests per account or combined, at your Brisbane hour — everything off until you say so",
        "✅ Activity log of everything that happened to the feed, with detail pages",
        "✅ Seamless links: a forwarded banking link shows a written teaser and signs the reader in on the same page",
      ],
      notes:
        "Balances are current-only from Redbark, so opening / closing balances for a past period are derived from the current balance. Pending rows are counted, never summed.",
    },
  ],
};

export default bankingModuleConfig;
